import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import {
  useStripe,
  useElements,
  CardElement,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import {
  GridColumn,
  Label,
  Icon,
  ButtonPrimary,
  H3,
  P,
  Footnote,
  LoaderInline,
} from "notes";
import {
  firebase,
  UserContext,
  EventContext,
  MessageContext,
} from "Components";
import "./stripe-field.css";
import { FieldMessage } from "./FieldMessage";

const initPayment = async (amount, eventId) => {
  const handler = firebase.functions().httpsCallable("payments-initPayment");
  const res = await handler({ amount, eventId, type: "donation" }).catch(
    (error) => {
      return { data: { success: false } };
    }
  );
  return res;
};

// Handler for the payment process
const confirmPayment = async (
  stripe,
  paymentIntentSecret,
  paymentMethod,
  confirmHandler
) => {
  let success = false;

  const finalizePayment = firebase
    .functions()
    .httpsCallable("payments-finalizePayment");

  const {
    paymentIntent,
    error: confirmError,
  } = await stripe.confirmCardPayment(
    paymentIntentSecret,
    { payment_method: paymentMethod },
    { handleActions: false }
  );

  if (confirmError) {
    // Report to the browser that the payment failed, prompting it to
    // re-show the payment interface, or show an error message and close
    // the payment interface.
    if (confirmHandler) {
      confirmHandler(false);
    }
  } else {
    // Report to the browser that the confirmation was successful, prompting
    // it to close the browser payment method collection interface.
    if (confirmHandler) {
      confirmHandler(true);
    }

    if (!paymentIntent) {
      success = false;
    } else {
      // Check if the PaymentIntent requires any actions and if so let Stripe.js
      // handle the flow. If using an API version older than "2019-02-11"
      // instead check for: `paymentIntent.status === "requires_source_action"`.
      if (paymentIntent.status === "requires_action") {
        // Let Stripe.js handle the rest of the payment flow.
        const { error } = await stripe.confirmCardPayment(paymentIntentSecret);
        if (error) {
          // The payment failed -- ask your customer for a new payment method.
          success = false;
        } else {
          // The payment has succeeded.
          success = true;
        }
      } else if (paymentIntent.status === "succeeded") {
        // The payment has succeeded.
        success = true;
      }
    }
  }

  if (success && paymentIntent) {
    await finalizePayment({ paymentIntentId: paymentIntent.id });
  }

  return success;
};

export const PaymentForm = ({ amount, onComplete }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useContext(UserContext);
  const { event } = useContext(EventContext);
  const { displayMessage } = useContext(MessageContext);
  const [paymentRequest, setPaymentRequest] = useState();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentIntentSecret, setPaymentIntentSecret] = useState();
  const eventId = event?.id;

  // Initialize payment intent
  useEffect(() => {
    if (!user) {
      return;
    }

    initPayment(amount, eventId).then((res) => {
      if (res.data.success) {
        setPaymentIntentSecret(res.data.paymentIntentSecret);
      }
    });
  }, [amount, eventId, user]);

  // Setup Payment Request (Apple Pay, Google Pay, etc)
  useEffect(() => {
    if (stripe && paymentIntentSecret) {
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Donation",
          amount: amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      // Check the availability of the Payment Request API.
      pr.canMakePayment().then((result) => {
        if (result) {
          pr.on("paymentmethod", async (ev) => {
            await confirmPayment(
              stripe,
              paymentIntentSecret,
              ev.paymentMethod.id,
              (success) => {
                // This is needed to close the browser dialog box
                if (!success) {
                  ev.complete("fail");
                  setError(true);
                } else {
                  ev.complete("success");
                  onComplete();
                  displayMessage({
                    color: "green",
                    children: `Thanks for your donation of $${(
                      amount / 100
                    ).toFixed(2)}`,
                    timeout: 5000,
                  });
                }
              }
            );
          });

          setPaymentRequest(pr);
        }
      });
    }
  }, [stripe, amount, paymentIntentSecret]);

  const handleSubmit = async () => {
    if (!stripe || !elements || !paymentIntentSecret) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const card = elements.getElement(CardElement);
    if (!card) {
      return;
    }
    setLoading(true);
    await confirmPayment(stripe, paymentIntentSecret, { card }).then((val) => {
      if (val) {
        onComplete();
        setLoading(false);
        displayMessage({
          color: "green",
          children: `Thanks for your donation of $${(amount / 100).toFixed(2)}`,
          timeout: 5000,
        });
      } else {
        setLoading(false);
        setError(true);
      }
    });
  };

  return (
    <Wrapper>
      <ContainerRow style={{ marginTop: "16px" }}>
        <LabelLight>Subtotal</LabelLight>
        <P>${(amount / 100).toFixed(2)}</P>
      </ContainerRow>
      <TotalRow>
        <LabelBold>Pay Total</LabelBold>
        <H3>
          <span>USD</span>${(amount / 100).toFixed(2)}
        </H3>
      </TotalRow>
      <Container style={{ marginTop: "16px" }}>
        <LabelBold>
          <Icon name="Lock" /> Credit Card
        </LabelBold>
        {/* <Footnote>All transactions are secure and encrypted.</Footnote> */}
        <CardElement
          options={{
            classes: {
              base: "stripe-credit-card",
              complete: "stripe-credit-card--complete",
              focus: "stripe-credit-card--focus",
              invalid: "stripe-credit-card--invalid",
            },
            style: {
              base: {
                fontFamily: '"Open Sans", sans-serif',
                fontSize: "16px",
                color: "#424770",
                "::placeholder": {
                  color: "#aab7c4",
                },
              },
              invalid: {
                color: "#9e2146",
              },
            },
            disabled: false,
          }}
        />
      </Container>
      <Actions>
        <PayButton disabled={!stripe || loading} onClick={handleSubmit}>
          {loading ? <LoaderInline /> : "Pay"}
        </PayButton>
        {paymentRequest && (
          <WalletPayment>
            <P>Or</P>
            <PaymentRequestButtonElement options={{ paymentRequest }} />
          </WalletPayment>
        )}
      </Actions>
      <FieldMessage
        active={error}
        content="There was an issue processing your payment. Please try again."
      />
    </Wrapper>
  );
};

const LabelBold = styled(Label)`
  align-items: center;
  flex-flow: row nowrap;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 12px;
  line-height: 20px;
  flex-grow: 1;
  svg {
    margin-right: 8px;
  }
`;

const LabelLight = styled(LabelBold)`
  font-weight: 400;
  margin: 0;
`;

const Wrapper = styled(GridColumn)``;

const Container = styled(GridColumn)`
  & > ${Footnote} {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 300;
    margin: 0 0 8px 0;
  }

  ${(props) =>
    props.readonly &&
    `
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  `};
`;

const ContainerRow = styled(Container)`
  flex-direction: row;
  align-items: center;
  padding: 16px 0;
  ${P} {
    font-weight: 700;
    margin: 0;
  }
`;

const TotalRow = styled(ContainerRow)`
  border-top: 1px solid ${(props) => props.theme.palette.gray.lightest};
  border-bottom: 1px solid ${(props) => props.theme.palette.gray.lightest};
  margin-left: 0;
  ${LabelBold} {
    margin: 0;
  }
  ${H3} {
    font-weight: 900;
    font-size: 20px;
    line-height: 26px;
    margin: 0;
  }
  span {
    font-size: 14px;
    font-weight: 600;
    line-height: 21px;
    margin-right: 6px;
  }
`;

const Actions = styled(Container)`
  margin-top: 8px;
`;

const PayButton = styled(ButtonPrimary)`
  background-color: #0a2540;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-weight: 500;
  &&:hover,
  &&:focus {
    background-color: ${(props) =>
      props.disabled ? props.theme.palette.gray.lightest : "#0a2540"};
  }
`;

const WalletPayment = styled(GridColumn)`
  margin-top: 16px;
  ${P} {
    width: 100%;
    text-align: center;
    margin: 0 0 16px 0;
  }
`;
